import React,{useId,useMemo,useState} from 'react';
import {searchPacks} from '../utils/estimateSearch';

const money=v=>Number(v)%1?Number(v).toFixed(2):String(Number(v));
const matchLabel={quantity:'Size match',price:'MRP match',nearest:'Nearest size'};

// Search-as-you-type product picker for estimates. Choices:
//  - an existing catalog pack            -> onPick(pack)
//  - a size the catalog doesn't have     -> onCalibrate(calibration) to price it from the nearest pack
//  - free text not in the catalog        -> onCustom(text) to add it with a typed amount
export default function EstimateProductSearch({packs,warehouse,addedIds,onPick,onCalibrate,onCustom,disabled}){
  const [query,setQuery]=useState(''),[open,setOpen]=useState(false),[active,setActive]=useState(0);
  const listId=useId();
  const {matches,calibration}=useMemo(()=>searchPacks(packs,query,{warehouse}),[packs,query,warehouse]);
  const options=useMemo(()=>{
    if(!query.trim())return [];
    const list=[];
    if(calibration)list.push({kind:'calibrate',key:'calibrate',calibration});
    for(const m of matches)list.push({kind:'pack',key:'pack:'+m.pack.id,...m,added:addedIds.has(String(m.pack.id))});
    list.push({kind:'custom',key:'custom'});
    return list;
  },[query,matches,calibration,addedIds]);
  const choose=option=>{
    if(!option||option.added)return;
    if(option.kind==='pack')onPick(option.pack);
    else if(option.kind==='calibrate')onCalibrate(option.calibration);
    else onCustom(query.trim());
    setQuery('');setOpen(false);setActive(0);
  };
  const onKeyDown=e=>{
    if(e.key==='ArrowDown'){e.preventDefault();setOpen(true);setActive(i=>Math.min(i+1,options.length-1));}
    else if(e.key==='ArrowUp'){e.preventDefault();setActive(i=>Math.max(i-1,0));}
    else if(e.key==='Enter'&&open&&options.length){e.preventDefault();choose(options[Math.min(active,options.length-1)]);}
    else if(e.key==='Escape'){setOpen(false);}
  };
  const showList=open&&options.length>0;
  return <div className="relative w-full max-w-3xl">
    <input role="combobox" aria-label="Search products" aria-expanded={showList} aria-controls={listId} aria-autocomplete="list"
      aria-activedescendant={showList?`${listId}-${active}`:undefined} disabled={disabled}
      className="w-full rounded border p-2 bg-white" placeholder="Search brand, product, size (e.g. chik 5 rs, rice 25kg) or barcode"
      value={query} onChange={e=>{setQuery(e.target.value);setOpen(true);setActive(0);}} onFocus={()=>setOpen(true)} onBlur={()=>setTimeout(()=>setOpen(false),150)} onKeyDown={onKeyDown}/>
    {showList&&<ul id={listId} role="listbox" aria-label="Product suggestions" className="absolute z-20 mt-1 max-h-96 w-full overflow-auto rounded border bg-white shadow-lg">
      {options.map((o,i)=>{
        const base=`cursor-pointer px-3 py-2 border-b last:border-b-0 ${i===active?'bg-blue-50':''} ${o.added?'opacity-50 cursor-not-allowed':''}`;
        const select=e=>{e.preventDefault();choose(o);};
        if(o.kind==='calibrate'){const {reference,quantity,units}=o.calibration;return <li key={o.key} id={`${listId}-${i}`} role="option" aria-selected={i===active} className={`${base} text-indigo-800`} onMouseDown={select} onMouseEnter={()=>setActive(i)}>
          <b>Price {quantity} {units} of {reference.name}</b><div className="text-xs">Not in catalog — calibrate from the {reference.size} pack, then confirm the amount</div></li>;}
        if(o.kind==='custom')return <li key={o.key} id={`${listId}-${i}`} role="option" aria-selected={i===active} className={`${base} text-gray-700`} onMouseDown={select} onMouseEnter={()=>setActive(i)}>
          <b>+ Add “{query.trim()}” as a new product</b><div className="text-xs">Not in catalog — you'll be asked for the amount</div></li>;
        const stock=Number(o.pack.stock?.[warehouse]);
        return <li key={o.key} id={`${listId}-${i}`} role="option" aria-selected={i===active} aria-disabled={o.added} className={base} onMouseDown={select} onMouseEnter={()=>setActive(i)}>
          <div className="flex flex-wrap items-baseline gap-x-2"><b>{o.pack.name}</b><span>{o.pack.size}</span>{matchLabel[o.match]&&<span className="rounded bg-gray-100 px-1 text-xs">{matchLabel[o.match]}</span>}{o.added&&<span className="text-xs">Already added</span>}</div>
          <div className="text-xs text-gray-600">{[o.pack.brand,o.pack.barcode,o.pack.stock?(stock>0?`Stock ${stock}`:'Out of stock here'):null,o.meta.mrp!=null?`MRP ₹${money(o.meta.mrp)}`:('mrp' in o.pack?'No saved price':null)].filter(Boolean).join(' · ')}</div></li>;
      })}
    </ul>}
  </div>;
}
